import { useEffect, useRef, useState } from "react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { RequestAPayment, ChatBotInsert, IntelligenceRail } from "@areas";
import { ErrorBoundary, cn } from "@utils";
import { useChatbot, useAiFeatures, usePortalNotifications, useSmartNoteV2, useHomeV2 } from "@flags";
import { useSpacebarShortcut } from "@hooks";
import { Outlet, useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { Sidebar } from "./components/Sidebar";
import { MobileSidebar } from "./components/MobileSidebar";
import { ToasterPortal } from "./components/ToasterPortal";
import { CreateEditGroupModal } from "./components/CreateEditGroupModal";
import { SmartNoteModal } from "./components/SmartNoteModal";
import { SmartNoteModalV2 } from "./components/SmartNoteModalV2";
import SmartNoteFab from "./components/SmartNoteFab";
import NotificationToaster from "./components/NotificationToaster";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "portal.sidebar.collapsed";

export default function PortalTemplate() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    SIDEBAR_COLLAPSED_STORAGE_KEY,
    false,
  );
  const [railCollapsed, setRailCollapsed] = useLocalStorage(
    "portal.tasks.rail.collapsed",
    false,
  );
  const [railHovered, setRailHovered] = useState(false);
  const pointerInsideRef = useRef(false);
  // Hovering the collapsed rail floats the full sidebar over the page; only the persisted
  // collapse flag (not hover) drives the main content's left padding, so page content never shifts.
  const sidebarRailExpanded = sidebarCollapsed && railHovered;
  const sidebarVisuallyExpanded = !sidebarCollapsed || railHovered;
  const [requestPaymentOpen, setRequestPaymentOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [smartNoteOpen, setSmartNoteOpen] = useState(false);
  const [smartNoteCustomer, setSmartNoteCustomer] = useState(null);
  const chatbotEnabled = useChatbot();
  const smartNoteEnabled = useAiFeatures();
  const smartNoteV2Enabled = useSmartNoteV2();
  const homeV2Enabled = useHomeV2();
  const portalNotificationsEnabled = usePortalNotifications();
  const posthog = usePostHog();

  // Open the Smart Note flow, optionally pre-scoped to a customer (e.g. from a task's "view
  // context"). Spacebar passes none, so the user picks a customer inside.
  const openSmartNote = (customer = null) => {
    setSmartNoteCustomer(customer);
    setSmartNoteOpen(true);
  };
  const closeSmartNote = () => {
    setSmartNoteOpen(false);
    setSmartNoteCustomer(null);
  };

  // Space opens the Smart Note modal — only when the feature flag is on.
  useSpacebarShortcut(() => openSmartNote(), {
    enabled: smartNoteEnabled && !smartNoteOpen,
  });

  // On every route transition the intelligence rail collapses — except that without the Home V2
  // flag (the old Today's tasks) it's forced open on /tasks. Only on the transition, so a manual
  // toggle on a page still sticks. While it's open the floating Ontik button docks into the rail's
  // footer, so the FAB is hidden then.
  const pathname = useLocation().pathname;
  const railOpensOnTasks = !homeV2Enabled && pathname === "/tasks";
  // Settings renders as a focus-trapping modal on top of this template, which makes this global
  // toaster inert. SettingsTemplate mounts its own toaster inside the dialog, so hide this one there.
  // (The bell now lives in PageHeader, which renders inside the settings dialog too.)
  const onSettings = useLocation().pathname.startsWith("/settings");
  useEffect(() => {
    setRailCollapsed(!railOpensOnTasks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, railOpensOnTasks]);

  const handleNavigate = () => setSidebarOpen(false);
  const handleRequestPayment = () => {
    setRequestPaymentOpen(true);
    setSidebarOpen(false);
  };

  const handleAction = (action, item) => {
    setSidebarOpen(false);
    switch (action) {
      case "createGroup":
        posthog.capture("create_group_clicked");
        setCreateGroupOpen(true);
        break;
      case "editGroup":
        if (item?.to) {
          // Extract group ID from the route: /accounts/groups/{id}
          const groupId = item.to.split("/").pop();
          setEditingGroup({ id: groupId, name: item.name });
        }
        break;
      default:
        console.warn(`Unknown action: ${action}`);
    }
  };

  return (
    <div>
      <MobileSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onNavigate={handleNavigate}
        onRequestPayment={handleRequestPayment}
        onAction={handleAction}
      />

      {/* Static sidebar for desktop */}
      <div
        onMouseEnter={() => {
          pointerInsideRef.current = true;
          if (sidebarCollapsed) setRailHovered(true);
        }}
        onMouseLeave={() => {
          pointerInsideRef.current = false;
          setRailHovered(false);
        }}
        onFocus={() => sidebarCollapsed && setRailHovered(true)}
        onBlur={(e) => {
          // The merchant dropdown toggling can fire blur with a null relatedTarget;
          // don't collapse the hover flyout while the pointer is still over the rail.
          if (pointerInsideRef.current) return;
          if (!e.currentTarget.contains(e.relatedTarget)) setRailHovered(false);
        }}
        className={cn(
          "hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:flex-col bg-zinc-50",
          "transition-[width] duration-300 ease-in-out",
          sidebarVisuallyExpanded ? "lg:w-[17.2rem]" : "lg:w-[52px]",
          sidebarRailExpanded && "shadow-xl",
        )}
      >
        <Sidebar
          collapsed={!sidebarVisuallyExpanded}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() =>
            setSidebarCollapsed((collapsed) => !collapsed)
          }
          onNavigate={handleNavigate}
          onRequestPayment={handleRequestPayment}
          onAction={handleAction}
        />
      </div>

      <div
        className={cn(
          "transition-[padding] duration-300 ease-in-out",
          sidebarCollapsed ? "lg:pl-[52px]" : "lg:pl-[17.2rem]",
        )}
      >
        <div className="flex h-screen">
          <main className="flex min-w-0 flex-1 flex-col">
            <ErrorBoundary>
            <Outlet
              context={{
                openMenu: () => setSidebarOpen(true),
                openSmartNote: smartNoteEnabled ? openSmartNote : undefined,
              }}
            />
            <RequestAPayment
              isOpen={requestPaymentOpen}
              onClose={() => setRequestPaymentOpen(false)}
            />
            <CreateEditGroupModal
              isOpen={createGroupOpen || !!editingGroup}
              onClose={() => {
                setCreateGroupOpen(false);
                setEditingGroup(null);
              }}
              editingGroup={editingGroup}
            />
            {smartNoteEnabled && (
              <>
                <SmartNoteFab
                  onClick={() => openSmartNote()}
                  hidden={smartNoteOpen}
                />
                {smartNoteV2Enabled ? (
                  <SmartNoteModalV2
                    isOpen={smartNoteOpen}
                    onClose={closeSmartNote}
                    initialCustomer={smartNoteCustomer}
                  />
                ) : (
                  <SmartNoteModal
                    isOpen={smartNoteOpen}
                    onClose={closeSmartNote}
                    initialCustomer={smartNoteCustomer}
                  />
                )}
              </>
            )}
            {/* Temporarily hidden while the Ontik Smart Note flow is the primary AI entry point. */}
            {/* {chatbotEnabled && <ChatBotInsert />} */}
            {portalNotificationsEnabled && !onSettings && <NotificationToaster />}
            <ToasterPortal />
            </ErrorBoundary>
          </main>
          {smartNoteEnabled && (
            <IntelligenceRail
              collapsed={railCollapsed}
              onToggleCollapse={() => setRailCollapsed((c) => !c)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
